import React from 'react'
import { Company } from 'models'
import type { CompaniesReducerState } from '../types'
import { companiesActions, type CompaniesReducerAction } from '../reducer'
import {
  useChildrenList,
  useCompanySelectionActions,
  useSelectionDerivedState,
  useSelectionSideEffects,
} from './selection'

const useCompanySelection = (params: {
  state: CompaniesReducerState
  dispatch: React.Dispatch<CompaniesReducerAction>
  selectionLimit: number
  selectableChildren: Array<Company>
  childrenQuery: {
    data?: Array<Company>
    isSuccess: boolean
    isFetching: boolean
    isError: boolean
  }
  orphanQuery: {
    isFetching: boolean
  }
  isUsingRemoteChildren: boolean
  parentChildrenCount: number
  userCompany: Company | null
  b2cAutoSelectedChildId: string
  b2cAutoSelectedChild: Company | null
  isLoginB2C: boolean
  resetChildrenSearch: () => void
  setParentSearchValue: (value: string, options?: { skipCommit?: boolean }) => void
  setParentSearchFilterValue: (value: string) => void
  onResetWaitingFiles: () => void
}) => {
  const {
    state,
    dispatch,
    selectionLimit,
    selectableChildren,
    childrenQuery,
    orphanQuery,
    isUsingRemoteChildren,
    parentChildrenCount,
    userCompany,
    b2cAutoSelectedChildId,
    b2cAutoSelectedChild,
    isLoginB2C,
    resetChildrenSearch,
    setParentSearchValue,
    setParentSearchFilterValue,
    onResetWaitingFiles,
  } = params

  useChildrenList({
    dispatch,
    childrenQuery,
    isOrphanSelected: state.isOrphanSelected,
    isUsingRemoteChildren,
    selectionLimit,
    pendingSelection: state.pendingSelection,
    selectedChildren: state.selectedChildren,
    isAllAccountsSelected: state.isAllAccountsSelected,
    pendingAllAccountsSelection: state.pendingAllAccountsSelection,
    isLoginB2C,
  })

  React.useEffect(() => {
    if (state.isOrphanSelected && state.orphanCompanies.length > 0) {
      const sortedOrphanCompanies = [...state.orphanCompanies].sort((a, b) =>
        (a.commercialNameInt || '').localeCompare(b.commercialNameInt || ''),
      )
      dispatch({ type: companiesActions.SET_CHILDREN_COMPANIES, companies: sortedOrphanCompanies })
    }
  }, [dispatch, state.isOrphanSelected, state.orphanCompanies])

  const selectionActions = useCompanySelectionActions({
    state,
    dispatch,
    selectableChildren,
    selectionLimit,
    onResetWaitingFiles,
  })

  useSelectionSideEffects({
    state,
    dispatch,
    userCompany,
    b2cAutoSelectedChildId,
    b2cAutoSelectedChild,
    isLoginB2C,
    setParentSearchValue,
    setParentSearchFilterValue,
    resetChildrenSearch,
  })

  const derivedState = useSelectionDerivedState({
    state,
    parentChildrenCount,
    isUsingRemoteChildren,
    selectionLimit,
  })

  const effectiveChildrenLoading = state.isOrphanSelected
    ? orphanQuery.isFetching
    : childrenQuery.isFetching

  const setSelectedParent = React.useCallback(
    (entity: Company | null) => {
      dispatch({ type: companiesActions.SET_SELECTED_PARENT, entity })
      onResetWaitingFiles()
    },
    [dispatch, onResetWaitingFiles],
  )

  return {
    setSelectedParent,
    setPendingSelection: selectionActions.setPendingSelection,
    validateSelection: selectionActions.validateSelection,
    pendingSelectionState: selectionActions.pendingSelectionState,
    validatedSelectionState: selectionActions.validatedSelectionState,
    pendingSelectionIsAllAccounts: selectionActions.pendingSelectionIsAllAccounts,
    hasPendingSelection: derivedState.hasPendingSelection,
    canSelectAllChildren: derivedState.canSelectAllChildren,
    pendingSelectionLimitReached: derivedState.pendingSelectionLimitReached,
    effectiveChildrenLoading,
  }
}

export { useCompanySelection }
